import { ProductName } from 'Constants/products.constants';
import { OrganizationPillarTypes } from '../insurance.constants';
import {
  AnimalDetailsDto,
  CarDetailsDto,
  ChildDetailsDto,
  HomeBuyerDetailsDto,
  InsuranceDetailsDto,
  InsuranceDto,
  MotorcycleDetailsDto,
  NewChildDetailsDto,
} from '../types/insurance.dto.types';
import { InsuranceDetails } from '../types/insurance.types';
import { DetailsCwp } from '../types/insurance.types.cwp';
import {
  AnimalDetails,
  ChildDetails,
  NewChildDetails,
} from '../types/products';
import { HomeBuyerDetails } from '../types/products/homeBuyer.types';
import { mapCwpDetails } from './mapCwpDetails';

export async function mapToInsuranceDetails<
  T extends InsuranceDetailsDto = InsuranceDetailsDto,
>(dto: InsuranceDto<T>): Promise<InsuranceDetails> {
  const mappedCwpDetails = mapCwpDetails(dto.detailsCwp);

  if (dto.details.insuranceProduct === ProductName.ANIMAL) {
    return mapToAnimalInsurance(
      dto as InsuranceDto<AnimalDetailsDto>,
      mappedCwpDetails,
    );
  }
  if (dto.details.insuranceProduct === ProductName.CHILD) {
    return mapToChildDetails(
      dto as InsuranceDto<ChildDetailsDto>,
      mappedCwpDetails,
    );
  }
  if (dto.details.insuranceProduct === ProductName.NEW_CHILD) {
    return mapToNewChildDetails(
      dto as InsuranceDto<NewChildDetailsDto>,
      mappedCwpDetails,
    );
  }
  if (
    dto.details.insuranceProduct === ProductName.CAR ||
    dto.details.insuranceProduct === ProductName.MOTORCYCLE
  ) {
    return mapCarMotorcycleDetails(
      dto as InsuranceDto<MotorcycleDetailsDto | CarDetailsDto>,
      mappedCwpDetails,
    );
  }
  if (
    dto.details.insuranceProduct === ProductName.HOME_BUYER_HOUSE ||
    dto.details.insuranceProduct === ProductName.HOME_BUYER_HOUSE_CONTENTS
  ) {
    return mapToHomeBuyerInsurance(
      dto as InsuranceDto<HomeBuyerDetailsDto>,
      mappedCwpDetails,
    );
  }
  return {
    ...dto.details,
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product.organizationPillar,
    systemName: dto.product.systemName,
    canChange: dto.details.canChange,
    detailsCwp: mappedCwpDetails,
  };
}

const mapToHomeBuyerInsurance = (
  dto: InsuranceDto<HomeBuyerDetailsDto>,
  mappedCwpDetails: DetailsCwp,
): HomeBuyerDetails => {
  return {
    ...dto.details,
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product.organizationPillar,
    systemName: dto.product.systemName,
    canChange: dto.details.canChange,
    detailsCwp: mappedCwpDetails,
    homeBuyerFacade: dto.details.homeBuyerFacade,
  };
};

function mapToAnimalInsurance(
  dto: InsuranceDto<AnimalDetailsDto>,
  mappedCwpDetails: DetailsCwp,
): AnimalDetails {
  return {
    ...dto.details,
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product.organizationPillar,
    animalType: dto.coverages[0].code,
    detailsCwp: mappedCwpDetails,
  };
}
async function mapToChildDetails(
  dto: InsuranceDto<ChildDetailsDto>,
  mappedCwpDetails: DetailsCwp,
): Promise<ChildDetails> {
  return {
    ...dto.details,
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product.organizationPillar,
    systemName: dto.product.systemName,
    canChange: dto.details.canChange,
    coverage: dto.details.coverage,
    detailsCwp: mappedCwpDetails,
  };
}

function mapToNewChildDetails(
  dto: InsuranceDto<NewChildDetailsDto>,
  mappedCwpDetails: DetailsCwp,
): NewChildDetails {
  return {
    ...dto.details,
    coverage: dto.details.coverages[0],
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product.organizationPillar,
    detailsCwp: mappedCwpDetails,
  };
}

function mapCarMotorcycleDetails(
  dto: InsuranceDto<CarDetailsDto | MotorcycleDetailsDto>,
  mappedCwpDetails: DetailsCwp,
): InsuranceDetails {
  if (!dto.details.coverage) {
    throw new Error(
      `Auto insurance without coverage detected: policyNumber: ${dto.contractNumber}`,
    );
  }
  return {
    ...dto.details,
    coverage: dto.details.coverage,
    description: dto.coverages[0].insuredName,
    cancellationDate: dto.cancellationDate,
    validFromDate: dto.firstTimeValidDate,
    policyNumber: dto.contractNumber,
    productName: dto.product.productName,
    agreementProduct: dto.agreement.agreementProduct,
    monthlyCost: dto.monthlyCost,
    annualCost: dto.annualCost,
    outerIssueDate: dto.issueDate,
    insuranceStatus: dto.insuranceStatus,
    organizationPillar: dto.product
      .organizationPillar as OrganizationPillarTypes,
    systemName: dto.product.systemName,
    canChange: dto.details.canChange,
    detailsCwp: mappedCwpDetails,
  };
}
